import clsx from 'clsx'

interface SkeletonProps {
  className?: string
  variant?: 'text' | 'circular' | 'rectangular'
  width?: string | number
  height?: string | number
  lines?: number
}

export function Skeleton({
  className,
  variant = 'text',
  width,
  height,
  lines = 1,
}: SkeletonProps) {
  const baseClass = 'animate-pulse bg-star-dim/20 rounded'

  const variantClass = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  }[variant]

  const style: React.CSSProperties = {}
  if (width) style.width = typeof width === 'number' ? `${width}px` : width
  if (height) style.height = typeof height === 'number' ? `${height}px` : height

  if (lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={clsx(baseClass, variantClass, className)}
            style={{
              ...style,
              width: i === lines - 1 ? '75%' : style.width,
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      className={clsx(baseClass, variantClass, className)}
      style={style}
    />
  )
}

// Pre-built skeleton compositions for common patterns
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={clsx('glass-card p-4 sm:p-5', className)}>
      <div className="flex items-start gap-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1 space-y-2">
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={12} />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton lines={2} />
      </div>
    </div>
  )
}

export function SkeletonStatCard({ className }: { className?: string }) {
  return (
    <div className={clsx('glass-card p-5', className)}>
      <Skeleton width={80} height={12} className="mb-2" />
      <Skeleton width={100} height={32} className="mb-1" />
      <Skeleton width={60} height={12} />
    </div>
  )
}

export function SkeletonTableRow({ columns = 5 }: { columns?: number }) {
  return (
    <tr className="border-b border-star-dim/10">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="p-4">
          <Skeleton width={i === 0 ? '80%' : '60%'} height={16} />
        </td>
      ))}
    </tr>
  )
}

export function SkeletonQuestionCard() {
  return (
    <div className="glass-card p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <Skeleton width="80%" height={18} className="mb-2" />
          <div className="flex gap-2">
            <Skeleton width={60} height={20} variant="rectangular" />
            <Skeleton width={80} height={20} variant="rectangular" />
          </div>
        </div>
        <Skeleton width={50} height={24} variant="rectangular" />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div>
          <Skeleton width={40} height={10} className="mb-1" />
          <Skeleton width={50} height={18} />
        </div>
        <div>
          <Skeleton width={40} height={10} className="mb-1" />
          <Skeleton width={50} height={18} />
        </div>
        <div>
          <Skeleton width={40} height={10} className="mb-1" />
          <Skeleton width={50} height={18} />
        </div>
      </div>
    </div>
  )
}

export function SkeletonPositionRow() {
  return (
    <div className="p-4 border-b border-star-dim/10">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <Skeleton width="70%" height={16} className="mb-2" />
          <div className="flex gap-2">
            <Skeleton width={50} height={16} variant="rectangular" />
          </div>
        </div>
        <Skeleton width={60} height={20} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="text-center">
            <Skeleton width={30} height={10} className="mx-auto mb-1" />
            <Skeleton width={40} height={14} className="mx-auto" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function DashboardSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <Skeleton width={150} height={28} className="mb-2" />
          <Skeleton width={200} height={14} />
        </div>
        <Skeleton width={100} height={40} variant="rectangular" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStatCard key={i} />
        ))}
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Needs Attention */}
        <div className="glass-card overflow-hidden">
          <div className="px-6 py-4 border-b border-star-dim/20">
            <Skeleton width={140} height={20} />
          </div>
          <div className="divide-y divide-star-dim/10">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4">
                <Skeleton width="80%" height={14} className="mb-2" />
                <Skeleton width="50%" height={12} />
              </div>
            ))}
          </div>
        </div>

        {/* Risk Summary */}
        <div className="glass-card overflow-hidden">
          <div className="px-6 py-4 border-b border-star-dim/20">
            <Skeleton width={120} height={20} />
          </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-3 bg-void-50 rounded-lg">
                  <Skeleton width={80} height={10} className="mb-1" />
                  <Skeleton width={60} height={16} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Predictions */}
      <div className="glass-card overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-star-dim/20">
          <Skeleton width={160} height={20} />
        </div>
        <div className="divide-y divide-star-dim/10">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <Skeleton width="60%" height={16} className="mb-2" />
                  <Skeleton width={80} height={12} />
                </div>
                <div className="flex gap-4">
                  <Skeleton width={50} height={18} />
                  <Skeleton width={50} height={18} />
                  <Skeleton width={50} height={18} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function QuestionListSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <Skeleton width={180} height={28} className="mb-2" />
        <Skeleton width={140} height={14} />
      </div>

      {/* Quick filters */}
      <div className="flex gap-2 mb-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} width={80} height={32} variant="rectangular" />
        ))}
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <Skeleton width={200} height={44} variant="rectangular" className="flex-1" />
          <Skeleton width={150} height={44} variant="rectangular" />
          <Skeleton width={150} height={44} variant="rectangular" />
        </div>
      </div>

      {/* Grid */}
      <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonQuestionCard key={i} />
        ))}
      </div>
    </div>
  )
}

export function PositionsSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <Skeleton width={160} height={28} className="mb-2" />
          <Skeleton width={120} height={14} />
        </div>
        <div className="flex gap-3">
          <Skeleton width={100} height={44} variant="rectangular" />
          <Skeleton width={120} height={44} variant="rectangular" />
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="glass-card p-6 mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <Skeleton width={100} height={12} className="mb-2" />
              <Skeleton width={80} height={28} />
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="glass-card p-4">
          <Skeleton width={140} height={16} className="mb-4" />
          <div className="flex items-center gap-4">
            <Skeleton variant="circular" width={128} height={128} />
            <div className="flex-1 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton variant="circular" width={8} height={8} />
                  <Skeleton width={80} height={12} />
                  <Skeleton width={40} height={12} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <Skeleton width={120} height={16} className="mb-4" />
          <Skeleton height={128} variant="rectangular" />
        </div>
      </div>

      {/* Positions list */}
      <div className="glass-card overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonPositionRow key={i} />
        ))}
      </div>
    </div>
  )
}
